import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { inputClasses } from "../common/tailwindClassnames";

// ── Pure helpers (ported verbatim from experiments/jq-filter-builder.tsx) ──

function jqPath(p: string[]): string { return "." + p.join("."); }

function valAt(obj: any, path: string[]): any {
    let v = obj;
    for (const k of path) { if (v == null) return undefined; v = v[k]; }
    return v;
}

function flatPaths(obj: any, pfx: string[] = []): { path: string[]; value: any }[] {
    const r: { path: string[]; value: any }[] = [];
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        for (const [k, v] of Object.entries(obj)) {
            const p = [...pfx, k];
            r.push({ path: p, value: v });
            if (v && typeof v === "object" && !Array.isArray(v)) r.push(...flatPaths(v, p));
        }
    }
    return r;
}

function genJq(conds: Condition[], maps: Maps, addl: AdditionalField[]): string {
    const cp = conds.filter(c => c.field).map(c => {
        const p = "." + c.field;
        const cv = isNaN(Number(c.value)) ? JSON.stringify(c.value) : Number(c.value);
        switch (c.op) {
            case "not_null": return `${p} != null`;
            case "is_null": return `${p} == null`;
            case "equals": return `${p} == ${cv}`;
            case "not_equals": return `${p} != ${cv}`;
            case "gt": return `${p} > ${Number(c.value)}`;
            case "lt": return `${p} < ${Number(c.value)}`;
            case "contains": return `(${p} | tostring | contains(${JSON.stringify(c.value || "")}))`;
            default: return null;
        }
    }).filter(Boolean) as string[];

    const lines: string[] = [];
    if (maps.source) lines.push(`  source: ${jqPath(maps.source)}`);
    if (maps.recorded_at) lines.push(`  recorded_at: ${jqPath(maps.recorded_at)}`);
    if (maps.lat || maps.lon) {
        lines.push(`  location: { lat: ${maps.lat ? jqPath(maps.lat) : "null"}, lon: ${maps.lon ? jqPath(maps.lon) : "null"} }`);
    }
    const al = addl.filter(f => f.name && f.path).map(f => `    ${JSON.stringify(f.name)}: ${jqPath(f.path.split("."))}`);
    if (al.length) lines.push(`  "additional": {\n${al.join(",\n")}\n  }`);
    const body = "{\n" + lines.join(",\n") + "\n}";
    return cp.length ? `if ( ${cp.join(" and ")} ) then ${body} else empty end` : body;
}

function evalMapping(input: any, maps: Maps, addl: AdditionalField[], conds: Condition[]): any {
    for (const c of conds) {
        if (!c.field) continue;
        const v = valAt(input, c.field.split("."));
        const cv = isNaN(Number(c.value)) ? c.value : Number(c.value);
        switch (c.op) {
            case "not_null": if (v == null) return null; break;
            case "is_null": if (v != null) return null; break;
            case "equals": if (v !== cv) return null; break;
            case "not_equals": if (v === cv) return null; break;
            case "gt": if (!(v > Number(c.value))) return null; break;
            case "lt": if (!(v < Number(c.value))) return null; break;
            case "contains": if (!String(v).includes(c.value || "")) return null; break;
        }
    }
    const res: any = {};
    if (maps.source) res.source = valAt(input, maps.source);
    if (maps.recorded_at) res.recorded_at = valAt(input, maps.recorded_at);
    if (maps.lat || maps.lon) res.location = { lat: maps.lat ? valAt(input, maps.lat) : null, lon: maps.lon ? valAt(input, maps.lon) : null };
    const a: any = {};
    for (const f of addl) { if (f.name && f.path) a[f.name] = valAt(input, f.path.split(".")); }
    if (Object.keys(a).length) res.additional = a;
    return res;
}

// ── Types ──

type Maps = { source: string[] | null; recorded_at: string[] | null; lat: string[] | null; lon: string[] | null };
type AdditionalField = { name: string; path: string };
type Condition = { field: string; op: string; value: string };

// ── Parse builder-generated JQ back into state ──

function parseJqToBuilderState(jq: string): { maps: Maps; conds: Condition[]; addl: AdditionalField[] } | null {
    try {
        const trimmed = jq.trim();
        if (!trimmed) return null;

        let condStr = "";
        let body = trimmed;

        // Step 1: strip if/else wrapper
        const ifMatch = trimmed.match(/^if\s*\(\s*([\s\S]+?)\s*\)\s*then\s*([\s\S]+?)\s*else\s*empty\s*end\s*$/);
        if (ifMatch) {
            condStr = ifMatch[1];
            body = ifMatch[2].trim();
        }

        if (!body.startsWith("{")) return null;

        // Step 2: parse conditions
        const conds: Condition[] = [];
        if (condStr) {
            for (const part of condStr.split(" and ")) {
                const p = part.trim();
                const containsM = p.match(/^\(\.([\w.]+)\s*\|\s*tostring\s*\|\s*contains\("([^"]*)"\)\)$/);
                if (containsM) { conds.push({ field: containsM[1], op: "contains", value: containsM[2] }); continue; }
                const notNullM = p.match(/^\.([\w.]+)\s*!=\s*null$/);
                if (notNullM) { conds.push({ field: notNullM[1], op: "not_null", value: "" }); continue; }
                const isNullM = p.match(/^\.([\w.]+)\s*==\s*null$/);
                if (isNullM) { conds.push({ field: isNullM[1], op: "is_null", value: "" }); continue; }
                const compM = p.match(/^\.([\w.]+)\s*(==|!=|>|<)\s*(.+)$/);
                if (compM) {
                    const opMap: Record<string, string> = { "==": "equals", "!=": "not_equals", ">": "gt", "<": "lt" };
                    let val = compM[3].trim();
                    const strM = val.match(/^"([^"]*)"$/);
                    if (strM) val = strM[1];
                    conds.push({ field: compM[1], op: opMap[compM[2]], value: val });
                }
            }
        }

        // Step 3: parse core mappings
        const maps: Maps = { source: null, recorded_at: null, lat: null, lon: null };
        const srcM = body.match(/\bsource:\s*\.([\w.]+)/);
        if (srcM) maps.source = srcM[1].split(".");
        const recM = body.match(/\brecorded_at:\s*\.([\w.]+)/);
        if (recM) maps.recorded_at = recM[1].split(".");
        const locM = body.match(/\blocation:\s*\{\s*lat:\s*(?:\.([\w.]+)|null)\s*,\s*lon:\s*(?:\.([\w.]+)|null)\s*\}/);
        if (locM) {
            if (locM[1]) maps.lat = locM[1].split(".");
            if (locM[2]) maps.lon = locM[2].split(".");
        }

        // Step 4: parse additional fields
        const addl: AdditionalField[] = [];
        const addlM = body.match(/"additional":\s*\{([^}]+)\}/);
        if (addlM) {
            const re = /"([^"]+)":\s*\.([\w.]+)/g;
            let m;
            while ((m = re.exec(addlM[1])) !== null) addl.push({ name: m[1], path: m[2] });
        }

        const hasContent = maps.source || maps.recorded_at || maps.lat || maps.lon || addl.length || conds.length;
        return hasContent ? { maps, conds, addl } : null;
    } catch {
        return null;
    }
}

const OPS = [
    { v: "not_null", l: "is not null" }, { v: "is_null", l: "is null" },
    { v: "equals", l: "equals" }, { v: "not_equals", l: "≠" },
    { v: "gt", l: ">" }, { v: "lt", l: "<" }, { v: "contains", l: "contains" },
];

// ── Drag context ──

// ── Sub-components ──

type TreeNodeProps = {
    name: string;
    value: any;
    path: string[];
    depth: number;
    collapsed: Set<string>;
    toggle: (k: string) => void;
    readOnly: boolean;
    dragPathRef: React.MutableRefObject<string[] | null>;
};

function TreeNode({ name, value, path, depth, collapsed, toggle, readOnly, dragPathRef }: TreeNodeProps) {
    const isObj = value && typeof value === "object" && !Array.isArray(value);
    const isArr = Array.isArray(value);
    const isLeaf = !isObj && !isArr;
    const pk = path.join(".");

    const typeColor = typeof value === "number" ? "#6d97f7" : typeof value === "string" ? "#007a0b" : typeof value === "boolean" ? "#d6520e" : "#8f8f8f";
    const display = isObj ? `{${Object.keys(value as object).length}}` : isArr ? `[${(value as any[]).length}]` : JSON.stringify(value);

    const handleDragStart = (e: React.DragEvent) => {
        if (!isLeaf || readOnly) return;
        dragPathRef.current = path;
        e.dataTransfer.setData("text/plain", pk);
        e.dataTransfer.effectAllowed = "copy";
        const ghost = document.createElement("div");
        ghost.textContent = jqPath(path);
        ghost.style.cssText = "position:absolute;top:-100px;left:-100px;padding:4px 10px;background:#00520c;color:#fff;border-radius:6px;font:12px monospace;white-space:nowrap;";
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);
        requestAnimationFrame(() => document.body.removeChild(ghost));
    };

    return (
        <div>
            <div
                draggable={isLeaf && !readOnly}
                onDragStart={handleDragStart}
                onDragEnd={() => { dragPathRef.current = null; }}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono select-none ${isLeaf && !readOnly ? "cursor-grab hover:bg-light-blue" : "cursor-default"}`}
                style={{ paddingLeft: depth * 16 + 6 }}
            >
                {(isObj || isArr) ? (
                    <span onClick={() => toggle(pk)} className="cursor-pointer w-3.5 text-center text-medium-gray text-[10px]">
                        {collapsed.has(pk) ? "▶" : "▼"}
                    </span>
                ) : (
                    <span className="w-3.5 text-center text-[10px]" style={{ color: "#b0c4f7" }}>⠿</span>
                )}
                <span className="text-off-black font-medium">{name}</span>
                <span className="text-medium-gray">:</span>
                {isLeaf && <span className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: typeColor }}>{display}</span>}
                {!isLeaf && <span className="text-medium-gray text-[11px]">{display}</span>}
                {isLeaf && !readOnly && <span className="ml-auto text-[10px] text-medium-gray">drag →</span>}
            </div>
            {isObj && !collapsed.has(pk) && Object.entries(value as object).map(([k, v]) => (
                <TreeNode key={k} name={k} value={v} path={[...path, k]} depth={depth + 1} collapsed={collapsed} toggle={toggle} readOnly={readOnly} dragPathRef={dragPathRef} />
            ))}
        </div>
    );
}

type DropSlotProps = {
    label: string;
    mapping: string[] | null;
    sampleInput: any;
    onDrop: (path: string[]) => void;
    onClear: () => void;
    readOnly: boolean;
    dragPathRef: React.MutableRefObject<string[] | null>;
};

function DropSlot({ label, mapping, sampleInput, onDrop, onClear, readOnly, dragPathRef }: DropSlotProps) {
    const [over, setOver] = useState(false);
    const val = mapping ? valAt(sampleInput, mapping) : null;
    const { t } = useTranslation();

    return (
        <div className="flex items-center gap-2 py-1.5 border-b border-field-outline last:border-b-0">
            <span className="font-mono text-xs text-secondary-gray font-medium" style={{ minWidth: 130 }}>{label}</span>
            <span className="text-medium-gray">←</span>
            {mapping ? (
                <div className="bg-light-green rounded-md px-2.5 py-1 font-mono text-xs text-dark-green flex items-center gap-1.5 flex-1 min-h-[28px]">
                    <span className="flex-1">{jqPath(mapping)}</span>
                    <span className="text-medium-gray text-[11px] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">= {JSON.stringify(val)}</span>
                    {!readOnly && <button onClick={onClear} className="text-orange hover:text-logs-orange text-base leading-none px-0.5">×</button>}
                </div>
            ) : (
                <div
                    onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setOver(true); }}
                    onDragLeave={() => setOver(false)}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); setOver(false); if (dragPathRef.current) onDrop(dragPathRef.current); }}
                    className={`flex-1 border-2 border-dashed rounded-md px-3 py-1.5 min-h-[28px] text-xs text-center transition-all ${over ? "border-dark-green bg-light-green text-dark-green" : "border-field-outline text-medium-gray"}`}
                >
                    {over ? t("Drop here") : t("Drag a source field here")}
                </div>
            )}
        </div>
    );
}

type AdditionalRowProps = {
    field: AdditionalField;
    index: number;
    onUpdate: (i: number, f: AdditionalField) => void;
    onRemove: (i: number) => void;
    sampleInput: any;
    readOnly: boolean;
    dragPathRef: React.MutableRefObject<string[] | null>;
};

function AdditionalRow({ field, index, onUpdate, onRemove, sampleInput, readOnly, dragPathRef }: AdditionalRowProps) {
    const [over, setOver] = useState(false);
    const pathArr = field.path ? field.path.split(".") : null;
    const val = pathArr ? valAt(sampleInput, pathArr) : null;
    const { t } = useTranslation();

    return (
        <div className="flex gap-1.5 items-center mb-1.5">
            <input
                value={field.name}
                onChange={e => onUpdate(index, { ...field, name: e.target.value })}
                placeholder={t("field name")}
                disabled={readOnly}
                className={`${inputClasses.standard} font-mono text-xs w-28`}
            />
            <span className="text-medium-gray">←</span>
            {field.path ? (
                <div className="bg-light-green rounded-md px-2.5 py-1 font-mono text-xs text-dark-green flex items-center gap-1.5 flex-1 min-h-[28px]">
                    <span className="flex-1">.{field.path}</span>
                    <span className="text-medium-gray text-[11px] max-w-[90px] overflow-hidden text-ellipsis whitespace-nowrap">= {JSON.stringify(val)}</span>
                    {!readOnly && <button onClick={() => onUpdate(index, { ...field, path: "" })} className="text-orange hover:text-logs-orange text-base leading-none">×</button>}
                </div>
            ) : (
                <div
                    onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setOver(true); }}
                    onDragLeave={() => setOver(false)}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); setOver(false); if (dragPathRef.current) onUpdate(index, { ...field, path: dragPathRef.current.join(".") }); }}
                    className={`flex-1 border-2 border-dashed rounded-md px-3 py-1.5 min-h-[28px] text-xs text-center transition-all ${over ? "border-dark-green bg-light-green text-dark-green" : "border-field-outline text-medium-gray"}`}
                >
                    {over ? t("Drop here") : t("Drag source field")}
                </div>
            )}
            {!readOnly && (
                <button onClick={() => onRemove(index)} className="text-orange hover:text-logs-orange text-lg leading-none">×</button>
            )}
        </div>
    );
}

type ConditionRowProps = {
    c: Condition;
    i: number;
    onUpdate: (i: number, c: Condition) => void;
    onRemove: (i: number) => void;
    readOnly: boolean;
    dragPathRef: React.MutableRefObject<string[] | null>;
};

function ConditionRow({ c, i, onUpdate, onRemove, readOnly, dragPathRef }: ConditionRowProps) {
    const [over, setOver] = useState(false);
    const { t } = useTranslation();

    return (
        <div className="flex gap-1.5 items-center mb-1.5 flex-wrap">
            {c.field ? (
                <div className="bg-light-blue rounded px-2 py-0.5 font-mono text-xs text-dark-blue-800 flex items-center gap-1">
                    <span>.{c.field}</span>
                    {!readOnly && <button onClick={() => onUpdate(i, { ...c, field: "" })} className="text-medium-gray hover:text-off-black text-sm leading-none">×</button>}
                </div>
            ) : (
                <div
                    onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setOver(true); }}
                    onDragLeave={() => setOver(false)}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); setOver(false); if (dragPathRef.current) onUpdate(i, { ...c, field: dragPathRef.current.join(".") }); }}
                    className={`border-2 border-dashed rounded px-2.5 py-0.5 text-xs text-center transition-all min-w-[120px] ${over ? "border-dark-green bg-light-green text-dark-green" : "border-field-outline text-medium-gray"}`}
                >
                    {over ? t("Drop here") : t("Drag field")}
                </div>
            )}
            <select
                value={c.op}
                onChange={e => onUpdate(i, { ...c, op: e.target.value })}
                disabled={readOnly}
                className={`${inputClasses.standard} text-xs py-1`}
            >
                {OPS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            {!["not_null", "is_null"].includes(c.op) && (
                <input
                    value={c.value || ""}
                    onChange={e => onUpdate(i, { ...c, value: e.target.value })}
                    placeholder={t("value")}
                    disabled={readOnly}
                    className={`${inputClasses.standard} font-mono text-xs w-24`}
                />
            )}
            {!readOnly && (
                <button onClick={() => onRemove(i)} className="text-orange hover:text-logs-orange text-lg leading-none">×</button>
            )}
        </div>
    );
}

// ── Main component ──

export type JqFilterBuilderProps = {
    onGenerate: (jq: string) => void;
    readOnly?: boolean;
    initialJq?: string;
};

const JqFilterBuilder = ({ onGenerate, readOnly = false, initialJq }: JqFilterBuilderProps) => {
    const { t } = useTranslation();

    const dragPathRef = useRef<string[] | null>(null);

    const [input, setInput] = useState<any>({});
    const [rawInput, setRawInput] = useState("");
    const [editing, setEditing] = useState(false);
    const [inputErr, setInputErr] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [maps, setMaps] = useState<Maps>(
        () => parseJqToBuilderState(initialJq ?? "")?.maps ?? { source: null, recorded_at: null, lat: null, lon: null }
    );
    const [addl, setAddl] = useState<AdditionalField[]>(
        () => parseJqToBuilderState(initialJq ?? "")?.addl ?? []
    );
    const [conds, setConds] = useState<Condition[]>(
        () => parseJqToBuilderState(initialJq ?? "")?.conds ?? []
    );

    const hasInteracted = useRef(false);

    const allPaths = useMemo(() => flatPaths(input), [input]);
    const jq = useMemo(() => genJq(conds, maps, addl), [conds, maps, addl]);
    const preview = useMemo(() => { const r = evalMapping(input, maps, addl, conds); return r ? [r] : []; }, [input, maps, addl, conds]);

    useEffect(() => {
        if (hasInteracted.current) onGenerate(jq);
    }, [jq]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = useCallback((k: string) => setCollapsed(p => {
        const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n;
    }), []);

    const hasMappings = maps.source || maps.recorded_at || maps.lat || maps.lon || addl.some(f => f.name && f.path);

    const panelCls = "bg-white rounded-lg border border-field-outline overflow-hidden flex flex-col";
    const headerCls = "px-3.5 py-2.5 font-semibold text-sm text-off-black border-b border-field-outline bg-very-light-gray flex justify-between items-center";
    const btnSm = "text-xs px-2 py-0.5 rounded border border-field-outline text-secondary-gray hover:text-off-black transition-colors bg-white";
    const sectionLabel = "text-[11px] font-bold text-medium-gray uppercase tracking-wide mb-2";

    return (
        <div className="flex flex-col rounded-lg border border-field-outline overflow-hidden">
            <div className={headerCls}>
                <span>{t("Filter Builder")}</span>
            </div>

            {/* Source + Mappings panels */}
            <div className="grid grid-cols-2 gap-3 p-3">

                {/* Source Payload */}
                <div className={panelCls}>
                    <div className={headerCls}>
                        <span>{t("Source Payload")}</span>
                        {!readOnly && (
                            <button
                                onClick={() => { setEditing(!editing); if (!editing) setRawInput(JSON.stringify(input, null, 2)); }}
                                className={`${btnSm} ${editing ? "bg-dark-green text-white border-dark-green" : ""}`}
                                type="button"
                            >
                                {editing ? t("Edit JSON") + "…" : t("Edit JSON")}
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <div className="p-2.5 flex flex-col gap-2 flex-1">
                            <textarea
                                value={rawInput}
                                onChange={e => setRawInput(e.target.value)}
                                className={`${inputClasses.standard} flex-1 font-mono text-xs resize-none min-h-[160px]`}
                            />
                            {inputErr && <div className="text-sm text-status-red">{inputErr}</div>}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { try { setInput(JSON.parse(rawInput)); setInputErr(null); setEditing(false); } catch { setInputErr(t("Invalid JSON")); } }}
                                    className="px-4 py-1.5 rounded border-none bg-dark-green text-white text-xs cursor-pointer hover:bg-status-green transition-colors"
                                    type="button"
                                >
                                    {t("Apply")}
                                </button>
                                <button
                                    onClick={() => { setEditing(false); setInputErr(null); }}
                                    className={`${btnSm} px-4 py-1.5`}
                                    type="button"
                                >
                                    {t("Cancel")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="py-1.5 overflow-y-auto flex-1 max-h-[360px]">
                                {Object.keys(input).length === 0 ? (
                                    <div className="px-3.5 py-2 text-xs text-medium-gray italic">
                                        {!readOnly ? t('Click "Edit JSON" to add sample payload') : t("No sample payload")}
                                    </div>
                                ) : (
                                    Object.entries(input).map(([k, v]) => (
                                        <TreeNode key={k} name={k} value={v} path={[k]} depth={0} collapsed={collapsed} toggle={toggle} readOnly={readOnly} dragPathRef={dragPathRef} />
                                    ))
                                )}
                            </div>
                            {Object.keys(input).length > 0 && (
                                <div className="px-3.5 py-2 bg-very-light-gray border-t border-field-outline text-[11px] text-medium-gray flex items-center gap-1.5">
                                    <span style={{ color: "#b0c4f7" }}>⠿</span> {t("Drag leaf fields onto mapping slots on the right")}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Field Mappings */}
                <div className={panelCls}>
                    <div className={headerCls}><span>{t("Field Mappings")}</span></div>
                    <div className="px-3.5 py-3 overflow-y-auto flex-1">

                        {/* Conditions */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className={sectionLabel}>{t("Conditions")}</span>
                                {!readOnly && (
                                    <button onClick={() => { hasInteracted.current = true; setConds(p => [...p, { field: "", op: "not_null", value: "" }]); }} className={btnSm} type="button">
                                        {t("+ Add")}
                                    </button>
                                )}
                            </div>
                            {conds.length === 0 && <div className="text-xs text-medium-gray">{t("No conditions — all records pass through")}</div>}
                            {conds.map((c, i) => (
                                <ConditionRow key={i} c={c} i={i} readOnly={readOnly} dragPathRef={dragPathRef}
                                    onUpdate={(idx, v) => { hasInteracted.current = true; setConds(p => p.map((cc, j) => j === idx ? v : cc)); }}
                                    onRemove={idx => { hasInteracted.current = true; setConds(p => p.filter((_, j) => j !== idx)); }} />
                            ))}
                        </div>

                        {/* Core Fields */}
                        <div className="mb-4">
                            <span className={sectionLabel}>{t("Core Fields")}</span>
                            <DropSlot label="source" mapping={maps.source} sampleInput={input} readOnly={readOnly} dragPathRef={dragPathRef}
                                onDrop={p => { hasInteracted.current = true; setMaps(m => ({ ...m, source: p })); }} onClear={() => { hasInteracted.current = true; setMaps(m => ({ ...m, source: null })); }} />
                            <DropSlot label="recorded_at" mapping={maps.recorded_at} sampleInput={input} readOnly={readOnly} dragPathRef={dragPathRef}
                                onDrop={p => { hasInteracted.current = true; setMaps(m => ({ ...m, recorded_at: p })); }} onClear={() => { hasInteracted.current = true; setMaps(m => ({ ...m, recorded_at: null })); }} />
                            <DropSlot label="location.lat" mapping={maps.lat} sampleInput={input} readOnly={readOnly} dragPathRef={dragPathRef}
                                onDrop={p => { hasInteracted.current = true; setMaps(m => ({ ...m, lat: p })); }} onClear={() => { hasInteracted.current = true; setMaps(m => ({ ...m, lat: null })); }} />
                            <DropSlot label="location.lon" mapping={maps.lon} sampleInput={input} readOnly={readOnly} dragPathRef={dragPathRef}
                                onDrop={p => { hasInteracted.current = true; setMaps(m => ({ ...m, lon: p })); }} onClear={() => { hasInteracted.current = true; setMaps(m => ({ ...m, lon: null })); }} />
                        </div>

                        {/* Additional Fields */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className={sectionLabel}>{t("Additional Fields")}</span>
                                {!readOnly && (
                                    <button onClick={() => { hasInteracted.current = true; setAddl(p => [...p, { name: "", path: "" }]); }} className={btnSm} type="button">
                                        {t("+ Add Field")}
                                    </button>
                                )}
                            </div>
                            {addl.length === 0 && <div className="text-xs text-medium-gray">{t("No additional fields")}</div>}
                            {addl.map((f, i) => (
                                <AdditionalRow key={i} field={f} index={i} sampleInput={input} readOnly={readOnly} dragPathRef={dragPathRef}
                                    onUpdate={(idx, v) => { hasInteracted.current = true; setAddl(p => p.map((ff, j) => j === idx ? v : ff)); }}
                                    onRemove={idx => { hasInteracted.current = true; setAddl(p => p.filter((_, j) => j !== idx)); }} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Output Preview */}
            <div className="mx-3 mb-3 rounded-lg border border-field-outline overflow-hidden flex flex-col">
                <div className={`${headerCls} flex-shrink-0`}>
                    <span>{t("Output Preview")}</span>
                    {!hasMappings && <span className="text-xs text-medium-gray font-normal">{t("Map some fields to see output")}</span>}
                </div>
                <pre className="font-mono text-xs p-4 bg-secondary-background text-off-black whitespace-pre-wrap min-h-[60px] leading-relaxed m-0">
                    {hasMappings ? JSON.stringify(preview, null, 2) : ""}
                </pre>
            </div>
        </div>
    );
};

export default JqFilterBuilder;
